<script>
import { Form, Field, Toast, Dialog } from 'vant'
import ImageUploader from '@/fcl/components/user/ImageUploader'
import PickerSelector from '@/fcl/components/user/PickerSelector'
import TextSwitcher from '@/fcl/components/user/TextSwitcher'
import BottomToolbar from '@/fcl/components/user/BottomToolbar'
import Referee from '@/fcl/tbs/model/vo/Referee'
import { QNImageUtil } from '@/fcl/utils'

const referee = new Referee

export default {
    //--------------------------------------------------
	// component name
	//--------------------------------------------------
	name: 'org-referee-edit',

    //--------------------------------------------------
	// component components
	//--------------------------------------------------
    components: {
        'van-form': Form,
        'van-field': Field,
        'image-uploader': ImageUploader,
        'picker-selector': PickerSelector,
        'text-switcher': TextSwitcher,
        'bottom-toolbar': BottomToolbar,
    },

    //--------------------------------------------------
    // component emits
    //--------------------------------------------------
	emits: ['custom-event'],

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
    data() {
        return {
            referee: {
                id: null,
                organizationId: null,
                matchId: null,
                name: null,
                avatarUrl: null,
                avatar: null,
                phone: null,
                grade: null,
                gradeIndex: null,
                gender: null,
                sportTypeOptions: null,
                sportType: null,
                credentialNum: null,
                enabled: null,
            },
            gradeOptions: [],
            isEdit: null,
            locale: {}
        }
    },
    
    //--------------------------------------------------
    // computed setter and getter
    //--------------------------------------------------
    computed: {
        genderOptions() {
            return referee.genderOptions()
        },
        refereeGender() {
            return this.referee.gender ? this.referee.gender : referee.genderDefaultValue
        },
        statusEnabled() {
            if (this.referee.enabled == null) {
                return true
            }
            else {
                return this.referee.enabled
            }
        }
    },

    //--------------------------------------------------
    // component methods
    //--------------------------------------------------
    methods: {
        onSubmitBtnClick() {
            console.log('onSubmitBtnClick')
            if (!this.validateSubmitRequired()) {
                // disable to submit and toast the message
                if (!this.referee.name) {
                    Toast(this.locale['referee-name-required'])
                }
                else if (!this.referee.sportType) {
                    Toast(this.locale['referee-sport-type-required'])
                }
            }
            else {
                // enable to submit
                this.submitData()
            }
        },
        onDeleteBtnClick() {
            console.log('onDeleteBtnClick')
            Dialog.confirm({
                title: this.locale['delete-referee'],
                message: this.locale['delete-referee-confirm-message']
            }).then(() => {
                // on confirm
                this.$emit('custom-event', {
                    type: 'delete',
                    data: this.referee
                })
            }).catch(() => {
                // on cancel
            })
        },
        onAvatarUploaderDataChange(data) {
            console.log('onAvatarUploaderDataChange', this.isEdit, data)
            this.referee.avatarUrl = null
            this.referee.avatar = data
        },
        onGenderDataChange(data) {
            console.log('onGenderDataChange', data.value)
            this.referee.gender = data.value
        },
        onSportTypeDataChange(data) {
            console.log('onSportTypeDataChange', data.value)
            this.referee.sportType = data.value
            this.gradeOptions = referee.gradeOptions(this.referee.sportType)
            this.referee.grade = this.gradeOptions[0].value
        },
        onGradeDataChange(data) {
            console.log('onGradeDataChange', data.value)
            this.referee.grade = data.value
        },
        onCredentialNumInput(data) {
            console.log('onCredentialNumInput', data)
            this.referee.credentialNum = data.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase()
        },
        onEnabledDataChange(data) {
            this.referee.enabled = data
        },
        //--------------------------------------------------
        // private method
        //--------------------------------------------------
        /**
         * @private
         * Adjust the image url according to the width and height.
         * @param img The identifier string for the image source.
         * @param w The image width number.
         * @param h The image height number.
         * @return The completed full url string.
         */
		adjustImageSize(img, w, h) {
            // console.log('adjustImageSize', img, w, h)
            if (img) {
                return QNImageUtil.imageView(img, w, h)
            }
			else {
                return null
            }
		},
        validateSubmitRequired() {
            // console.log('validateSubmitRequired')
            if (this.referee.name 
                && this.referee.sportType) {
                return true
            }
            else {
                return false
            }
        },
        submitData() {
            console.log('submit data', this.referee)
            if (this.validateSubmitRequired()) {
                this.$emit('custom-event', {
                    type: 'save',
                    data: this.referee
                })
            }
        }
    },
}
</script>

<template>
    <div class="referee-edit">
        <van-form>
            <van-field 
                name="referee-name" 
                v-model="referee.name" 
                :label="locale['referee-name-label']" 
                :placeholder="locale['referee-name-placeholder']"
                :autofocus="!isEdit"
                clearable
                input-align="right"
                maxlength="30" 
            />
            <image-uploader
                :label="locale['referee-avatar-label']"
                :description="locale['referee-avatar-placeholder']"
                :oversizeMax="5000*1024"
                :oversizeHint="locale['upload-image-oversize']"
                :imageUrl="adjustImageSize(this.referee.avatarUrl, 400, 400)"
                @change-data="onAvatarUploaderDataChange"
            />
            <picker-selector
                :label="locale['gender']"
                :options="genderOptions"
                :value="refereeGender"
                @change-data="onGenderDataChange"
            />
            <van-field 
                name="referee-phone" 
                v-model="referee.phone" 
                :label="locale['referee-phone-label']"
                clearable
                input-align="right"
                type="digit"
                maxlength="11" 
            />
            <picker-selector
                :label="locale['referee-sport-type-label']"
                :options="referee.sportTypeOptions"
                :value="referee.sportType"
                @change-data="onSportTypeDataChange"
            />
            <picker-selector
                :label="locale['referee-grade-label']"
                :options="gradeOptions"
                :value="referee.grade"
                :index="referee.gradeIndex"
                @change-data="onGradeDataChange"
            />
            <van-field 
                name="referee-credential-num" 
                v-model="referee.credentialNum" 
                :label="locale['referee-credential-num-label']"
                :placeholder="locale['referee-credential-num-placeholder']"
                clearable
                input-align="right"
                maxlength="20" 
                @input="onCredentialNumInput"
            />
            <text-switcher 
                :label="locale['referee-status-label']"
                :checked="statusEnabled"
                @change-data="onEnabledDataChange"
            />
        </van-form>
        <bottom-toolbar 
            v-if="!isEdit"
            type="button" 
            :mainButtonLabel="locale['submit']"
            :mainButtonDisabled="!validateSubmitRequired()"
            @click-main-button="onSubmitBtnClick"
        />
        <bottom-toolbar 
            v-else
            type="button-button" 
            :mainButtonLabel="locale['submit']"
            :mainButtonDisabled="!validateSubmitRequired()"
            :minorButtonLabel="locale['delete']"
            @click-main-button="onSubmitBtnClick"
            @click-minor-button="onDeleteBtnClick"
        />
    </div>
</template>
<style>
.referee-edit .van-field__label {
    width: 120px;
}
</style>